import { KeyboardKey } from "assets/js/enums/keyboard.enum";
import { useEffect, useRef, useState } from "react";

export interface ColumnToggle {
	id: string;
	label: string;
}

interface ColumnVisibilityMenuProps {
	columns: ColumnToggle[];
	/** A missing entry counts as visible, matching DatagridV2. */
	visibility: Record<string, boolean>;
	onChange: (visibility: Record<string, boolean>) => void;
}

export function ColumnVisibilityMenu({ columns, visibility, onChange }: ColumnVisibilityMenuProps) {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isOpen === false) {
			return;
		}

		const closeOnOutsideClick = (event: MouseEvent) => {
			if (containerRef.current?.contains(event.target as Node) === false) {
				setIsOpen(false);
			}
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === KeyboardKey.Escape) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", closeOnOutsideClick);
		document.addEventListener("keydown", closeOnEscape);

		return () => {
			document.removeEventListener("mousedown", closeOnOutsideClick);
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [isOpen]);

	const isVisible = (id: string) => visibility[id] !== false;
	const visibleCount = columns.filter((column) => isVisible(column.id)).length;
	const hiddenCount = columns.length - visibleCount;

	const toggleColumn = (id: string) => {
		if (isVisible(id) && visibleCount === 1) {
			return;
		}

		onChange({ ...visibility, [id]: isVisible(id) === false });
	};

	const showAll = () => {
		const allVisible: Record<string, boolean> = {};
		for (const column of columns) {
			allVisible[column.id] = true;
		}
		onChange(allVisible);
	};

	return (
		<div className="columnMenu" ref={containerRef}>
			<button className="columnMenu-trigger" type="button" onClick={() => setIsOpen(isOpen === false)}>
				Columns
				{hiddenCount > 0 ? <span className="columnMenu-badge">{hiddenCount} hidden</span> : null}
			</button>

			{isOpen ? (
				<div className="columnMenu-panel">
					<div className="columnMenu-list">
						{columns.map((column) => (
							<label className="columnMenu-item" key={column.id}>
								<input
									type="checkbox"
									checked={isVisible(column.id)}
									onChange={() => toggleColumn(column.id)}
									disabled={isVisible(column.id) && visibleCount === 1}
								/>
								{column.label}
							</label>
						))}
					</div>
					<button className="columnMenu-showAll" type="button" onClick={showAll} disabled={hiddenCount === 0}>
						Show all
					</button>
				</div>
			) : null}
		</div>
	);
}
